import React, { useMemo } from "react";
import PropTypes from "prop-types";

import TruncatedWithTooltip from "../../../TruncatedWithTooltip";
import { UIStatusTag, UITable } from "../../../ui";
import { clx } from "../../../../utils/Helpers";
import { formatReportDateParts } from "./reportMappers";

import "./ReportsShared.scss";

const EMPTY_CELL = "-";

const emptyCell = () => (
  <span className="MuiDataGrid-cellContent">{EMPTY_CELL}</span>
);

const cellText = (value) => {
  const next = value || EMPTY_CELL;
  if (next === EMPTY_CELL) return emptyCell();
  return (
    <div className="reports-cell-truncate">
      <TruncatedWithTooltip title={next}>
        <span className="MuiDataGrid-cellContent" dir="auto">
          {next}
        </span>
      </TruncatedWithTooltip>
    </div>
  );
};

const DateTimeCell = ({ value }) => {
  if (!value || value === EMPTY_CELL) {
    return emptyCell();
  }

  const parts = formatReportDateParts(value);
  if (!parts?.date) {
    return cellText(value);
  }

  return (
    <div className="reports-datetime">
      <span className="reports-datetime__date">{parts.date}</span>
      {parts.time ? (
        <span className="reports-datetime__time">{parts.time}</span>
      ) : null}
    </div>
  );
};

DateTimeCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const ReportsTable = ({ rows, labels, pageSize, variant, onRowClick }) => {
  const isEventScoped = variant === "event";

  const columns = useMemo(() => {
    const cols = [
      {
        field: "title",
        headerName: labels.reportTitle,
        flex: 1.2,
        minWidth: 180,
        sortable: false,
        renderCell: (params) => cellText(params.value),
      },
    ];

    if (!isEventScoped) {
      cols.push({
        field: "eventName",
        headerName: labels.eventName,
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: (params) => cellText(params.value),
      });
    }

    cols.push(
      {
        field: "zoneName",
        headerName: labels.zone,
        flex: 0.8,
        minWidth: 120,
        sortable: false,
        renderCell: (params) => cellText(params.value),
      },
      {
        field: "issuerName",
        headerName: labels.name,
        flex: 0.9,
        minWidth: 140,
        sortable: false,
        renderCell: (params) => cellText(params.value),
      },
      {
        field: "issuerRole",
        headerName: labels.role,
        flex: 0.9,
        minWidth: 140,
        sortable: false,
        renderCell: (params) => cellText(params.value),
      },
      {
        field: "dateTime",
        headerName: isEventScoped
          ? labels.createdDateTime || labels.dateTime
          : labels.dateTime,
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => <DateTimeCell value={params.value} />,
      },
    );

    if (isEventScoped) {
      cols.push({
        field: "closedDateTime",
        headerName: labels.closedDateTime || labels.dateTime,
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => {
          const status = String(params.row?.status || "").toLowerCase();
          if (status === "open") {
            return emptyCell();
          }
          return <DateTimeCell value={params.value} />;
        },
      });
    }

    cols.push({
      field: "status",
      headerName: labels.status,
      flex: 0.6,
      minWidth: 140,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        const status = String(params.value || "").toLowerCase();
        if (status !== "open" && status !== "closed") return emptyCell();
        return (
          <UIStatusTag
            status={status}
            label={status === "open" ? labels.open : labels.closed}
          />
        );
      },
    });

    return cols;
  }, [isEventScoped, labels]);

  return (
    <div
      className={clx(
        "reports-list-table",
        onRowClick && "reports-list-table--clickable",
      )}
    >
      <UITable
        rows={rows}
        columns={columns}
        pageSize={pageSize}
        contentSized
        hideFooter
        disableSelectionOnClick
        getRowId={(row) => row.id}
        onRowClick={
          onRowClick ? ({ row }) => onRowClick(row) : undefined
        }
      />
    </div>
  );
};

ReportsTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  labels: PropTypes.object.isRequired,
  pageSize: PropTypes.number.isRequired,
  variant: PropTypes.oneOf(["global", "event"]),
  onRowClick: PropTypes.func,
};

ReportsTable.defaultProps = {
  variant: "global",
  onRowClick: undefined,
};

export default ReportsTable;
